import { Dayjs } from 'dayjs';
import {
  EntityStatus,
  ErrorType,
  JourneyReliefEventType,
  JourneyStatus,
  ServiceType,
} from './core';
import { isTranslationRecord, TranslationRecord } from './language';

export interface Journey {
  id: string;
  operatorId: string;
  routeId: string | null;
  contractId: string | null;
  serviceType: ServiceType;
  departurePortId: string;
  arrivalPortId: string;
  day: string;
  time: string;
  journeyStatus: JourneyStatus;
  entityStatus: EntityStatus;
  journeyReliefEventType: JourneyReliefEventType | null;
}

export type JourneyInfo = Omit<Journey, 'id'>;

export type NewJourney = Omit<
  JourneyInfo,
  'journeyStatus' | 'entityStatus' | 'journeyReliefEventType'
>;

export type JourneyFormValues = {
  contractId: string | null;
  serviceType: ServiceType | null;
  departurePortId: string | null;
  arrivalPortId: string | null;
  day: Dayjs | null;
  time: Dayjs | null;
};

export interface JourneyFormSubmittedValues {
  operatorId: string;
  contractId:
    | string
    | null /* TODO Remove the nullable typing, as it is not supposed to be allowed */;
  serviceType: ServiceType;
  departurePortId: string;
  arrivalPortId: string;
  day: Dayjs;
  time: Dayjs;
}

export enum JourneyChangeNoteType {
  Cancel = 'cancel',
  ChangeTime = 'changeTime',
  ChangePort = 'changePort',
  ChangeTimeAndPort = 'changeTimeAndPort',
  ReliefEvent = 'reliefEvent',
}

export type JourneyChangeResponse = {
  journey: Journey;
  note: {
    id: string;
    type: JourneyChangeNoteType;
    note: string;
    creationDate: string;
  };
};

export type JourneyChangePayload = {
  journeyId: string | null;
  journey: Omit<
    JourneyChangeResponse['journey'],
    'id' | 'journeyStatus' | 'entityStatus' | 'journeyReliefEventType'
  >;
  note: {
    note: string;
    type: JourneyChangeNoteType;
    reliefEventType: JourneyReliefEventType | null;
  };
};

export interface JourneyPlaceholder {
  journeyId: string | null;
  journeyStatus: JourneyStatus;
  entityStatus: EntityStatus;
  operatorId: string;
  operatorName: string;
  contractId: string | null;
  contractName: string | null;
  serviceType: ServiceType;
  routeId: string | null;
  day: string; // YYYY-MM-DD format
  time: string; // HH:mm:ss format
  departurePortId: string;
  departurePortName: TranslationRecord;
  arrivalPortId: string;
  arrivalPortName: TranslationRecord;
}

export const isJourneyPlaceholder = (
  data: unknown,
): data is JourneyPlaceholder => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return false;
  }
  const {
    journeyId,
    journeyStatus,
    entityStatus,
    operatorId,
    operatorName,
    contractId,
    contractName,
    serviceType,
    routeId,
    day,
    time,
    departurePortId,
    departurePortName,
    arrivalPortId,
    arrivalPortName,
  } = data as Record<PropertyKey, unknown>;

  return Boolean(
    (journeyId === null || typeof journeyId === 'string') &&
      Object.values(JourneyStatus).includes(journeyStatus as JourneyStatus) &&
      Object.values(EntityStatus).includes(entityStatus as EntityStatus) &&
      typeof operatorId === 'string' &&
      typeof operatorName === 'string' &&
      (contractId === null || typeof contractId === 'string') &&
      (contractName === null || typeof contractName === 'string') &&
      Object.values(ServiceType).includes(serviceType as ServiceType) &&
      (routeId === null || typeof routeId === 'string') &&
      typeof day === 'string' &&
      typeof time === 'string' &&
      typeof departurePortId === 'string' &&
      departurePortName &&
      typeof departurePortName === 'object' &&
      isTranslationRecord(departurePortName as TranslationRecord) &&
      typeof arrivalPortId === 'string' &&
      arrivalPortName &&
      typeof arrivalPortName === 'object' &&
      isTranslationRecord(arrivalPortName as TranslationRecord),
  );
};

export interface VesselJourneyLog {
  vesselId: string;
  fuel: number;
  cargoAmount: number | null;
  noOfVehicles: number | null;
}

export interface PassengersJourneyLog {
  skipperId: string;
  crew: string[] | null;
  adultIslander: number;
  adultNonIslander: number;
  underEighteenIslander: number;
  underEighteenNonIslander: number;
  underFiveIslander: number;
  underFiveNonIslander: number;
  studentsIslander: number;
  studentsNonIslander: number;
  overSixtyIslander: number;
  overSixtyNonIslander: number;
}

export interface JourneyLogValidationEntry {
  type: ErrorType;
  message: string;
}

export interface JourneyLog {
  journeyId: string;
  journeyStatus: JourneyStatus;
  entityStatus: EntityStatus;
  journeyLog: VesselJourneyLog;
  passengerDetails: PassengersJourneyLog;
  validation: JourneyLogValidationEntry[];
}

export interface UpdatedJourneyLog {
  journeyId: string | null;
  journey: NewJourney;
  log: VesselJourneyLog;
  passengerDetails: PassengersJourneyLog;
}
